import React from 'react';
import { avatarSrc, type StaffAvatarManifest } from '../../utils/avatarUrl';
import {
  useStaffAvatarBaseUrl,
  useStaffAvatarManifest,
  useStaffAvatarsLive,
} from './StaffAvatarManifest';
import { userInitials } from './userInitials';
import styles from './UserAvatar.module.css';

/** 'fill' takes the size of its container, for a host that sets the box itself. */
export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'fill';

interface UserAvatarProps {
  readonly email?: string | null;
  readonly name?: string | null;
  readonly size?: UserAvatarSize;
  /** Accessible name, for an avatar with no visible name beside it. */
  readonly label?: string;
  /**
   * The photo this surface showed before the staff manifest, used until the manifest is
   * live. Only for surfaces that already had photos; new surfaces leave it out.
   */
  readonly legacySrc?: string | null;
  /**
   * The colours of the initials, for a surface whose people are not all one brand's: the same person wears the same
   * ones everywhere. Left out, the initials are the brand green. A photo covers them either way.
   */
  readonly tint?: UserAvatarTint;
}

/** A background and the text drawn on it, as CSS colours. */
export interface UserAvatarTint {
  readonly background: string;
  readonly color: string;
}

/**
 * A Cowbell employee's photo, or their initials.
 *
 * The photo is requested only when the staff manifest lists it, so someone without a photo
 * costs no request and no console error. Decorative when the name sits beside it; pass
 * `label` when it stands alone.
 */
export const UserAvatar = React.memo(function UserAvatar({
  email,
  name,
  size = 'sm',
  label,
  legacySrc,
  tint,
}: UserAvatarProps) {
  const manifest = useStaffAvatarManifest();
  const isLive = useStaffAvatarsLive();
  const baseUrl = useStaffAvatarBaseUrl();
  const src = pickAvatarSrc({ isLive, manifest, email, legacySrc, baseUrl });
  // A photo deleted since the manifest was fetched: one failed request, then initials.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);

  return (
    <span
      className={`${styles.avatar} ${styles[size]}`}
      style={tint}
      {...avatarA11yProps(label)}
    >
      {userInitials(name, email)}
      {src && src !== failedSrc && (
        <img
          className={styles.photo}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailedSrc(src)}
        />
      )}
    </span>
  );
});

interface StaffAvatarLabelProps {
  readonly email?: string | null;
  readonly name?: string | null;
  readonly size?: UserAvatarSize;
  readonly children: React.ReactNode;
}

/**
 * Puts a staff avatar before existing content. Outside the Cowbell persona, or with no one
 * to show, it renders the content alone, exactly as before.
 */
export function StaffAvatarLabel({
  email,
  name,
  size = 'sm',
  children,
}: StaffAvatarLabelProps) {
  const manifest = useStaffAvatarManifest();
  if (!showsStaffAvatar(manifest !== undefined, email, name)) {
    return <>{children}</>;
  }
  const gapClass = size === 'xs' ? ` ${styles.labelXs}` : '';
  return (
    <span className={`${styles.label}${gapClass}`}>
      <UserAvatar email={email} name={name} size={size} />
      <span className={styles.text}>{children}</span>
    </span>
  );
}

/** Whether a staff surface gets an avatar: Cowbell persona, and someone to show. */
export function showsStaffAvatar(
  isCowbellPersona: boolean,
  email?: string | null,
  name?: string | null
) {
  return isCowbellPersona && Boolean(email?.trim() || name?.trim());
}

/** Decorative beside a visible name; an image named `label` when it stands alone. */
function avatarA11yProps(label?: string) {
  if (label) {
    return { role: 'img', 'aria-label': label };
  }
  return { 'aria-hidden': true };
}

/**
 * Which photo to load: the manifest's once it is live; before that, only the photo a
 * surface already showed. Null means initials and no request.
 */
export function pickAvatarSrc({
  isLive,
  manifest,
  email,
  legacySrc,
  baseUrl,
}: Readonly<{
  isLive: boolean;
  manifest: StaffAvatarManifest | undefined;
  email?: string | null;
  legacySrc?: string | null;
  baseUrl?: string;
}>): string | null {
  if (isLive && manifest) {
    return avatarSrc(email, manifest, baseUrl);
  }
  return legacySrc || null;
}
